import { Attachment, Drawer, Tag } from '@billy/design-system'
import { useTranslation } from 'react-i18next'

import { useUpdateAttachment } from '@features/attachments/hooks/useUpdateAttachment'
import { billStatusToTagColor } from '@features/bills/constants/billStatusToTagColor'
import { billStatusToTitleKey } from '@features/bills/constants/billStatusToTitleKey'
import { BillType } from '@features/bills/enums/billType'
import { useDeleteBillAttachment } from '@features/bills/hooks/useDeleteBillAttachment'
import { useInvalidateBills } from '@features/bills/hooks/useInvalidateBills'
import { useUploadBillAttachments } from '@features/bills/hooks/useUploadBillAttachments'
import { Bill } from '@features/bills/types/bill'
import { getBillStatus } from '@features/bills/utils/getBillStatus'
import { useFetchContactById } from '@features/contacts/hooks/useFetchContactById'
import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { useInvalidateTransactions } from '@features/transactions/hooks/useInvalidateTransactions'

import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useTransactionStaticActions } from '../../../../hooks/useTransactionStaticActions'
import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import { AttachmentsSection } from '../../elements/AttachmentsSection'
import { BookingsSection } from '../../elements/BookingsSection/BookingsSection'
import { DescriptionSection } from '../../elements/DescriptionSection'
import { DetailsSection } from '../../elements/DetailsSection'
import { HeroSection } from '../../elements/HeroSection'
import { Separator } from '../../elements/Separator'
import { TransactionDrawerSkeleton } from '../../elements/TransactionDrawerSkeleton'

interface BillDrawerViewProps {
  bill: Bill
}

export const BillDrawerView = ({ bill }: BillDrawerViewProps) => {
  const { t } = useTranslation()
  const { isVoidedEntry, transactionId, transactionNo } = useTransactionDrawerContext()

  const {
    amount,
    attachments,
    grossAmount,
    balance,
    balanceModifiers,
    contactId,
    createdTime,
    currencyId,
    dueDate,
    entryDate,
    id,
    isPaid,
    state,
    tax,
    type,
    voucherNo,
  } = bill

  const { contact, isLoading } = useFetchContactById(contactId)
  const { invalidateBill } = useInvalidateBills()
  const { invalidateTransactions } = useInvalidateTransactions()
  const { update, isLoading: isLinking } = useUpdateAttachment()
  const { handleFilesDropped, isUploading } = useUploadBillAttachments({ billId: id })
  const { deleteAttachment } = useDeleteBillAttachment(id)
  const { handleSelect, items, modals } = useTransactionStaticActions({
    isVoided: isVoidedEntry,
    originatorId: id,
    originatorType: TransactionOriginatorType.Bill,
    transactionId,
  })

  const isCreditNote = type === BillType.CreditNote
  const additionalTag = isCreditNote ? <Tag type="subtle">{t('credit_note')}</Tag> : null

  if (isLoading) {
    return <TransactionDrawerSkeleton transactionNo={transactionNo} />
  }

  const handleAttachmentSelect = (attachment: Attachment) => {
    update(
      { id: attachment.id, ownerReference: `bill:${id}`, ownerId: id },
      {
        onSuccess: () => {
          invalidateBill(id)
          invalidateTransactions()
        },
      },
    )
  }

  const billStatus = getBillStatus({
    amount,
    balance,
    dueDate: dueDate ? new Date(dueDate) : undefined,
    isPaid,
    state,
    tax,
  })

  const statusTag = isCreditNote
    ? undefined
    : {
        color: billStatusToTagColor[billStatus],
        label: t(billStatusToTitleKey[billStatus]),
      }

  const paymentDate = balanceModifiers?.[0]?.entryDate

  return (
    <>
      <Drawer.Header
        actions={items}
        closable
        onAction={handleSelect}
        title={t('ledger.drawer_title', { number: transactionNo })}
      />
      <Drawer.Body withPadding={false} gap="0">
        <HeroSection
          additionalTag={additionalTag}
          amount={grossAmount}
          contactName={contact?.name}
          currency={currencyId}
          entryDate={entryDate}
          source={t('ledger.voucher_no', { number: voucherNo })}
          status={statusTag}
        />
        <Separator />
        <BookingsSection originatorCurrency={currencyId} />
        <Separator />
        <DescriptionSection />
        <Separator />
        <DetailsSection number={voucherNo} createdTime={createdTime} paymentDate={paymentDate} />
        <Separator />
        <AttachmentsSection
          attachments={attachments}
          isLoading={isUploading || isLinking}
          isVoided={isVoidedEntry}
          onAttachmentSelect={handleAttachmentSelect}
          onFilesDropped={handleFilesDropped}
          onRemove={deleteAttachment}
          scope={Scope.BillAttachmentWrite}
          trackingContext={TrackingContext.BillUpload}
        />
      </Drawer.Body>
      {modals}
    </>
  )
}
